import React, { useState, ChangeEvent, useEffect } from "react";
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridRenderCellParams
} from "@mui/x-data-grid";
import {
  TextField,
  Button,
  Container,
  Box,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  InputLabel,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { SERVER_BASE_URL } from "../Config";
import { useNavigate } from "react-router-dom";
import { formatStatus, formatDeliveryMethod, AVAILABLE_STATUSES } from "../utils/statusUtils";

const formatDate = (dateString: string | null) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/New_York"
  });
};

const formatDateShort = (dateString: string | null) => {
  if (!dateString) return "";
  // Append T12:00:00 to date-only strings to avoid timezone shifting the date back a day
  const normalized = dateString.includes('T') ? dateString : dateString + 'T12:00:00';
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};


type RowData = {
  id: number;
  orderDate: string;
  needByDate: string;
  isAsap: boolean;
  patientName: string;
  drugName: string;
  status: string;
  lastUpdated: string;
  deliveryMethod: string;
};

const ProviderOrderHistory: React.FC = () => {
  const [rows, setRows] = useState<GridRowsProp<RowData>>([]);
  const [filteredRows, setFilteredRows] = useState<GridRowsProp<RowData>>([]);
  const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem("providerSearchQuery") || "");
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const saved = localStorage.getItem("providerStatusFilter");
    return saved ? JSON.parse(saved) : [];
  });
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const loadOrders = async () => {
    try {
      const providerToken = localStorage.getItem("providerToken");
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate.format("YYYY-MM-DD"));
      if (endDate) params.set("end_date", endDate.format("YYYY-MM-DD"));
      const response = await fetch(`${SERVER_BASE_URL}/provider_orders?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${providerToken}`,
        },
      });
      if (!response.ok) return;
      const data = await response.json();

      const transformedRows = data.orders.map((order: any) => ({
        id: order.id,
        orderDate: order.created,
        needByDate: order.needBy,
        isAsap: order.isAsap,
        patientName: order.patient?.name || "Unknown",
        drugName: order.products?.[0]?.product?.name || "Unknown",
        status: order.status,
        lastUpdated: order.lastUpdated,
        deliveryMethod: order.deliveryMethod || "Unknown",
      }));

      setRows(transformedRows);
    } catch (err) {
      console.error("Failed to load orders:", err);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [startDate, endDate]);

  useEffect(() => {
    // Apply initial filters when rows are set or statusFilter changes
    if (rows.length > 0) {
      applyFilters(searchQuery, statusFilter);
    }
  }, [rows, statusFilter]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    localStorage.setItem("providerSearchQuery", query);
    applyFilters(query, statusFilter);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    localStorage.setItem("providerSearchQuery", "");
    applyFilters("", statusFilter);
  };


  const navigate = useNavigate();

  const handleViewClick = (row: RowData) => {
    navigate(`/history/${row.id}`);
  };

  const handleDoubleClick = (row: RowData) => {
    navigate(`/history/${row.id}`);
  };

  const applyFilters = (query = searchQuery, statuses = statusFilter) => {
    let filtered = rows;

    // Apply search filter
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter((row) =>
        row.patientName.toLowerCase().includes(lowerQuery) ||
        row.drugName.toLowerCase().includes(lowerQuery) ||
        row.id.toString().includes(lowerQuery)
      );
    }

    // Apply status filter
    if (statuses.length > 0) {
      filtered = filtered.filter((row) => statuses.includes(row.status));
    }

    filtered = [...filtered].sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
    setFilteredRows(filtered);
  };

  const handleStatusFilterChange = (event: any) => {
    const value = event.target.value as string[];
    setStatusFilter(value);
    localStorage.setItem("providerStatusFilter", JSON.stringify(value));
    applyFilters(searchQuery, value);
  };


  const renderStatusChip = (status: string) => {
    const label = formatStatus(status);
    const chipStyles: Record<string, { backgroundColor: string; color: string }> = {
      PENDING: { backgroundColor: "#ed6c02", color: "#fff" },
      DELIVERED: { backgroundColor: "#2e7d32", color: "#fff" },
      PROCESSING_EXCEPTION: { backgroundColor: "#1976d2", color: "#fff" },
      CANCELLED: { backgroundColor: "#d32f2f", color: "#fff" },
      CANCELLATION_REQUESTED: { backgroundColor: "#fff", color: "#d32f2f" },
    };
    const style = chipStyles[status] || {};
    return <Chip label={label} size="small" sx={{ ...style, border: status === "CANCELLATION_REQUESTED" ? "1px solid #d32f2f" : undefined }} />;
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "Order #", width: 100 },
    { 
      field: "orderDate", 
      headerName: "Order Date", 
      flex: 1,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: "needByDate",
      headerName: "Need By",
      flex: 1,
      renderCell: (params) => {
        const row = params.row as RowData;
        return row.isAsap ? (
          <Chip label="ASAP" size="small" color="error" />
        ) : (
          formatDateShort(params.value)
        );
      }
    },
    { field: "patientName", headerName: "Patient", flex: 1 },
    { field: "drugName", headerName: "Drug", flex: 1 },
    { field: "deliveryMethod", headerName: "Delivery", flex: 1, renderCell: (params) =>
      params.value === "shipToPatient"
        ? <Chip label="Ship to Patient" size="small" sx={{ backgroundColor: '#ffeb3b', color: '#000' }} />
        : formatDeliveryMethod(params.value)
    },
    { 
      field: "status", 
      headerName: "Status", 
      flex: 1,
      renderCell: (params) => renderStatusChip(params.value)
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      flex: 1,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          color="primary"
          onClick={() => handleViewClick(params.row as RowData)}
        >
          <VisibilityIcon />
        </IconButton>
      )
    }
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Order History
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Search orders..."
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <FormControl sx={{ minWidth: { xs: 0, sm: 200 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status"
              renderValue={(selected) => {
                const count = (selected as string[]).length;
                return count === 0 ? "All Statuses" : `${count} status${count === 1 ? '' : 'es'} selected`;
              }}
            >
              {AVAILABLE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={statusFilter.indexOf(status) > -1} />
                  <ListItemText primary={formatStatus(status)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              maxDate={dayjs()}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: { sx: { minWidth: { xs: 0, sm: 150 } } },
                field: { clearable: true },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              maxDate={dayjs()}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: { sx: { minWidth: { xs: 0, sm: 150 } } },
                field: { clearable: true },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          </LocalizationProvider>

        </Stack>

        <Stack direction="row" spacing={1} mt={1} mb={2} flexWrap="wrap" useFlexGap>
          {searchQuery && (
            <Chip label={searchQuery} onDelete={handleClearSearch} sx={{ backgroundColor: "#d32f2f", color: "#fff", "& .MuiChip-deleteIcon": { color: "#fff" } }} />
          )}
        </Stack>

        {isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {filteredRows.map((row) => (
              <Card key={row.id} variant="outlined">
                <CardActionArea onClick={() => handleViewClick(row)}>
                  <CardContent sx={{ textAlign: "left" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {row.patientName}
                      </Typography>
                      {renderStatusChip(row.status)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {row.drugName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ordered: {formatDateShort(row.orderDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Delivery: {row.deliveryMethod === "shipToPatient"
                        ? <Chip label="Ship to Patient" size="small" sx={{ backgroundColor: '#ffeb3b', color: '#000' }} />
                        : formatDeliveryMethod(row.deliveryMethod)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Order #{row.id}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
            {filteredRows.length === 0 && (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No orders found.
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              onRowDoubleClick={(params) => handleDoubleClick(params.row as RowData)}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                sorting: {
                  sortModel: [{ field: "orderDate", sort: "desc" }]
                },
                pagination: {
                  paginationModel: { pageSize: 25 }
                }
              }}
              sx={{
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer"
                }
              }}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ProviderOrderHistory;